import { memo, useCallback } from 'react';

import { useTranslation } from '@hooks/useTranslation';
import TextInput from '@components/ui/Input/TextInput';
import Select from '@workpay/ds-shared/components/ui/Select';
import MultiSelect from '@workpay/ds-shared/components/ui/MultiSelect';
import Toggle from '@components/ui/Input/Toggle';
import DatePicker from '@components/ui/DatePicker/DatePicker';
import type { FilterFieldConfig, FilterValue } from '@/types/filters';
import styles from './FilterPanel.module.scss';

// ============================================
// TYPES
// ============================================

interface FilterFieldProps {
  config: FilterFieldConfig;
  value: FilterValue | undefined;
  /** Whether this filter is currently applied (persisted in URL) */
  isApplied?: boolean;
  onChange: (value: FilterValue | undefined) => void;
}

// ============================================
// COMPONENT
// ============================================

function FilterField({ config, value, isApplied, onChange }: FilterFieldProps) {
  const { t } = useTranslation();
  const { type, label, options, placeholder, fromLabel, toLabel } = config;

  // Select handler
  const handleSelectChange = useCallback(
    (val: string) => {
      onChange(val || undefined);
    },
    [onChange],
  );

  // MultiSelect handler
  const handleMultiSelectChange = useCallback(
    (val: string[]) => {
      onChange(val.length > 0 ? val : undefined);
    },
    [onChange],
  );

  // Text handler
  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      onChange(val || undefined);
    },
    [onChange],
  );

  // Toggle handler
  const handleToggleChange = useCallback(
    (checked: boolean) => {
      // Only set value when checked; uncheck removes the filter
      onChange(checked ? true : undefined);
    },
    [onChange],
  );

  // Date range handlers
  const handleFromDateChange = useCallback(
    (date: string) => {
      const current = (typeof value === 'object' && !Array.isArray(value) ? value : {}) as {
        gte?: string;
        lte?: string;
      };
      const next = { ...current, gte: date || undefined };
      if (!next.gte && !next.lte) {
        onChange(undefined);
      } else {
        onChange(next);
      }
    },
    [value, onChange],
  );

  const handleToDateChange = useCallback(
    (date: string) => {
      const current = (typeof value === 'object' && !Array.isArray(value) ? value : {}) as {
        gte?: string;
        lte?: string;
      };
      const next = { ...current, lte: date || undefined };
      if (!next.gte && !next.lte) {
        onChange(undefined);
      } else {
        onChange(next);
      }
    },
    [value, onChange],
  );

  const dot = isApplied ? <span className={styles.appliedDot} /> : null;

  switch (type) {
    case 'select':
      return (
        <div className={styles.fieldRow}>
          <span className={styles.fieldLabel}>{dot}{label}</span>
          <Select
            options={options ?? []}
            value={typeof value === 'string' ? value : ''}
            onChange={handleSelectChange}
            placeholder={placeholder ?? label}
            size="sm"
            searchable
          />
        </div>
      );

    case 'multi-select':
      return (
        <div className={styles.fieldRow}>
          <span className={styles.fieldLabel}>{dot}{label}</span>
          <MultiSelect
            options={options ?? []}
            value={Array.isArray(value) ? value : []}
            onChange={handleMultiSelectChange}
            placeholder={placeholder ?? label}
            size="sm"
            searchable
          />
        </div>
      );

    case 'text':
      return (
        <div className={styles.fieldRow}>
          <span className={styles.fieldLabel}>{dot}{label}</span>
          <TextInput
            value={typeof value === 'string' ? value : ''}
            onChange={handleTextChange}
            placeholder={placeholder ?? label}
            size="sm"
          />
        </div>
      );

    case 'toggle':
      return (
        <div className={styles.fieldRow}>
          <span className={styles.fieldLabel}>{dot}{label}</span>
          <Toggle
            isChecked={value === true}
            onChange={handleToggleChange}
          />
        </div>
      );

    case 'date-range': {
      const rangeVal = (typeof value === 'object' && !Array.isArray(value) ? value : {}) as {
        gte?: string;
        lte?: string;
      };
      return (
        <div className={styles.fieldRow}>
          <span className={styles.fieldLabel}>{dot}{label}</span>
          <div className={styles.dateRangeRow}>
            <DatePicker
              label={fromLabel ?? t('K_LABEL_FROM', 'From')}
              value={rangeVal.gte ?? ''}
              onChange={handleFromDateChange}
              size="sm"
              maxDate={rangeVal.lte}
            />
            <DatePicker
              label={toLabel ?? t('K_LABEL_TO', 'To')}
              value={rangeVal.lte ?? ''}
              onChange={handleToDateChange}
              size="sm"
              minDate={rangeVal.gte}
            />
          </div>
        </div>
      );
    }

    default:
      return null;
  }
}

export default memo(FilterField);
